/**
 * How healthy a reported setting is. A handset that can't be located or can't
 * raise an alert is the thing an admin is looking for on this screen, so every
 * field is classified rather than just printed.
 *
 * `neutral` is for purely informational fields (battery state, storage
 * permissions) that have no good or bad value.
 *
 * `notApplicable` means the field can't exist on this OS version — the
 * permission was introduced later. That is materially different from `unknown`,
 * which means the OS *does* have the field but the app didn't report it,
 * usually an older app build.
 */
export type AppDeviceSettingHealth =
    | 'good'
    | 'warning'
    | 'bad'
    | 'neutral'
    | 'notApplicable'
    | 'unknown';

export type AppDeviceFieldDescriptor = {
    /**
     * The leaf name within its settings group, and the suffix of its i18n
     * label. With `derive` set this is a name of our own rather than an API
     * field, since the row stands for several of them.
     */
    key: string;
    /**
     * Collapses several API fields into the one thing the user was actually
     * shown — Android grants a whole permission group from a single prompt, so
     * listing each member as its own row invents a distinction the OS doesn't
     * make. Returns undefined when none of its sources were reported.
     */
    derive?: (values: Record<string, string | undefined>) => string | undefined;
    health: (value: string) => AppDeviceSettingHealth;
    /**
     * The OS release this permission was introduced in, as the platform
     * reports it — an iOS version ('16.0') or an Android release ('13'), not an
     * API level. Below this, the field is `notApplicable` rather than missing.
     * Omit for fields that exist on every version we support.
     */
    minOsVersion?: string;
    /**
     * The last OS release this permission applies to, inclusive. Above it the
     * field is `notApplicable` — used for permissions the platform has since
     * replaced, such as Android's legacy BLUETOOTH / BLUETOOTH_ADMIN, which
     * give way to BLUETOOTH_CONNECT / BLUETOOTH_SCAN in Android 12.
     */
    maxOsVersion?: string;
};

export type AppDeviceGroupDescriptor = {
    /** The settings sub-object name, e.g. 'location'. */
    key: string;
    fields: AppDeviceFieldDescriptor[];
};

/** The settings tree, read loosely — see the note in app-devices-section.tsx. */
export type AppDeviceSettingsValues =
    | Record<string, Record<string, string | undefined> | undefined>
    | undefined;

/**
 * Compares dotted version strings ('17.2.1', '15.1', '14'), padding the shorter
 * with zeros. Returns a negative number when `a` is older than `b`.
 */
export function compareOsVersions(a: string, b: string): number {
    const aParts = a.split('.').map(Number);
    const bParts = b.split('.').map(Number);

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aPart = aParts[i] ?? 0;
        const bPart = bParts[i] ?? 0;
        // A version we can't parse shouldn't silently read as 0 and mark every
        // field not-applicable, so bail out and let the caller treat it as
        // unknown rather than guessing.
        if (Number.isNaN(aPart) || Number.isNaN(bPart)) return NaN;
        if (aPart !== bPart) return aPart - bPart;
    }
    return 0;
}

/** Whether this OS version has the permission at all. */
function appliesToOsVersion(
    field: AppDeviceFieldDescriptor,
    osVersion: string
): boolean {
    if (field.minOsVersion !== undefined) {
        const comparison = compareOsVersions(osVersion, field.minOsVersion);
        // An unparseable version compares as NaN; assume the field applies
        // rather than hiding it on a guess.
        if (!Number.isNaN(comparison) && comparison < 0) return false;
    }

    if (field.maxOsVersion !== undefined) {
        const comparison = compareOsVersions(osVersion, field.maxOsVersion);
        if (!Number.isNaN(comparison) && comparison > 0) return false;
    }

    return true;
}

/**
 * Classifies one field for one device. A field this OS version can't have is
 * `notApplicable`; a field the OS has but the app didn't send is `unknown`.
 *
 * Applicability is checked before the value, so a legacy permission an app
 * still reports on a newer OS is treated as not applicable rather than
 * classified — Android keeps answering the old BLUETOOTH permission query on 12+
 * even though it no longer governs anything.
 */
export function evaluateField(
    field: AppDeviceFieldDescriptor,
    values: Record<string, string | undefined> | undefined,
    osVersion: string
): AppDeviceSettingHealth {
    if (!appliesToOsVersion(field, osVersion)) {
        return 'notApplicable';
    }

    const value = resolveFieldValue(field, values);
    return value === undefined ? 'unknown' : field.health(value);
}

/** The reported value for a field, via `derive` where the row covers several. */
export function resolveFieldValue(
    field: AppDeviceFieldDescriptor,
    values: Record<string, string | undefined> | undefined
): string | undefined {
    if (values === undefined) {
        return undefined;
    }
    return field.derive ? field.derive(values) : values[field.key];
}

/**
 * Grants that Android hands out together as a permission group. Reported as
 * granted only when every source that was reported is granted, so a partial
 * grant — only reachable through adb or an OEM quirk — reads as not granted
 * rather than silently passing.
 */
export function allGranted(
    values: Record<string, string | undefined>,
    keys: string[]
): string | undefined {
    const reported = keys
        .map((key) => values[key])
        .filter((value): value is string => value !== undefined);

    if (reported.length === 0) {
        return undefined;
    }
    return reported.every((value) => value === 'yes') ? 'yes' : 'no';
}

/**
 * The worst classification across every field, so a table row can say whether
 * it is worth opening. Fields the OS can't have, and fields the app didn't
 * report, are both ignored — neither means the handset is misconfigured.
 */
export function getWorstHealth(
    groups: AppDeviceGroupDescriptor[],
    settings: AppDeviceSettingsValues,
    osVersion: string
): AppDeviceSettingHealth {
    if (settings === undefined) {
        return 'unknown';
    }

    let worst: AppDeviceSettingHealth = 'good';
    for (const group of groups) {
        const values = settings[group.key];
        if (values === undefined) continue;

        for (const field of group.fields) {
            const health = evaluateField(field, values, osVersion);
            if (health === 'bad') return 'bad';
            if (health === 'warning') worst = 'warning';
        }
    }
    return worst;
}

// Most fields are plain yes/no strings, so these keep the tables below terse.
const yesIsGood = (value: string): AppDeviceSettingHealth =>
    value === 'yes' ? 'good' : 'bad';

const yesIsGoodNoIsWarning = (value: string): AppDeviceSettingHealth =>
    value === 'yes' ? 'good' : 'warning';

/** For optional capabilities: worth showing as on, but not a fault when off. */
const yesIsGoodOtherwiseNeutral = (value: string): AppDeviceSettingHealth =>
    value === 'yes' ? 'good' : 'neutral';

const noIsGood = (value: string): AppDeviceSettingHealth =>
    value === 'no' ? 'good' : 'warning';

const noIsGoodYesIsBad = (value: string): AppDeviceSettingHealth =>
    value === 'no' ? 'good' : 'bad';

const neutral = (): AppDeviceSettingHealth => 'neutral';

/** Builds a classifier from explicit value lists; anything unlisted is unknown. */
function oneOf(options: {
    good?: string[];
    warning?: string[];
    bad?: string[];
}): (value: string) => AppDeviceSettingHealth {
    return (value) => {
        if (options.good?.includes(value)) return 'good';
        if (options.warning?.includes(value)) return 'warning';
        if (options.bad?.includes(value)) return 'bad';
        return 'unknown';
    };
}

export const IOS_GROUPS: AppDeviceGroupDescriptor[] = [
    /**
     * iOS Location. Every field here has existed since iOS 14 —
     * `accuracyAuthorization` is the newest — so none needs version gating at
     * our iOS 15.1 floor.
     *
     * `authorizedWhenInUse` is NOT flagged: iOS still tracks well enough under
     * it, so Always is a nice-to-have rather than a requirement, and some
     * customers turn it down deliberately. Only a genuine loss of location is
     * flagged:
     *   - `reducedAccuracy` is coarse enough that responders can't be sent to a
     *     useful location, which defeats the point of an alert.
     *   - `notDetermined` means the prompt has never been answered, so there is
     *     no location at all.
     */
    {
        key: 'location',
        fields: [
            { key: 'servicesEnabled', health: yesIsGood },
            {
                key: 'permission',
                health: oneOf({
                    good: ['authorizedAlways', 'authorizedWhenInUse'],
                    bad: ['denied', 'restricted', 'notDetermined']
                })
            },
            {
                key: 'accuracyAuthorization',
                health: oneOf({
                    good: ['fullAccuracy'],
                    bad: ['reducedAccuracy']
                })
            }
        ]
    },
    /**
     * iOS Bluetooth. Only needed to pair with the wearable we sell, which many
     * customers don't have — so nothing here is ever flagged as a fault. It is
     * reported so support can confirm pairing is possible, no more than that.
     *
     * `authorization` has existed since iOS 13, so it needs no gating.
     */
    {
        key: 'bluetooth',
        fields: [
            {
                key: 'authorization',
                health: (value) =>
                    value === 'allowedAlways' ? 'good' : 'neutral'
            }
        ]
    },
    /**
     * iOS Notifications. All of these predate iOS 15.1, so none needs gating.
     *
     * A task reminder the worker never sees is the failure this whole screen
     * exists to catch, so the bar here is higher than elsewhere:
     *   - `provisional` and `ephemeral` deliver silently to Notification Centre
     *     with no banner or sound. Technically authorised, practically invisible
     *     for something time-critical, so both are treated as a failure rather
     *     than a caution.
     *   - `alert: no` removes the banner; `sound: no` removes the audible cue.
     *     Either alone can mean a timeout passes unnoticed.
     *   - `critical` is NOT flagged when off: notifications still arrive, they
     *     just respect Silent and Do Not Disturb, and it is an opt-in the user
     *     controls per reminder in the Preferences tab. Shown for information
     *     so support can see whether alerts will break through.
     *   - `badge` is the icon count — cosmetic, never flagged.
     */
    {
        key: 'notifications',
        fields: [
            {
                key: 'authorization',
                health: oneOf({
                    good: ['authorized'],
                    bad: ['denied', 'notDetermined', 'provisional', 'ephemeral']
                })
            },
            { key: 'alert', health: yesIsGood },
            { key: 'sound', health: yesIsGood },
            { key: 'critical', health: yesIsGoodOtherwiseNeutral },
            { key: 'badge', health: neutral }
        ]
    },
    /**
     * iOS Battery & Power. Only Low Power Mode is listed: it throttles
     * background work, so it affects whether a reminder arrives on time.
     *
     * `batteryState` and `batteryLevel` are also reported but deliberately not
     * shown — they are the charge at the moment of the last check-in, which may
     * be days old, so they describe a moment rather than a configuration and
     * would go stale on screen.
     */
    {
        key: 'batteryAndPower',
        fields: [{ key: 'lowPowerMode', health: noIsGood }]
    }
];

export const ANDROID_GROUPS: AppDeviceGroupDescriptor[] = [
    /**
     * Android Location. At our Android 7.0 floor:
     *   - `servicesEnabled`, `coarse` and `fine` exist on every version.
     *   - `background` (ACCESS_BACKGROUND_LOCATION) is Android 10+. Before that
     *     background access came free with foreground permission, so on 7-9 it
     *     is genuinely not applicable rather than missing.
     *
     * `fine: no` covers two cases that are equally bad for us: outright denial,
     * and — from Android 12 — the user picking "Approximate" at the prompt.
     * That second case is the direct analogue of iOS `reducedAccuracy`, and is
     * classified the same way.
     *
     * `background` is NOT flagged when off. The app runs a foreground service,
     * so locations still arrive while it is minimised, and some customers turn
     * background access off deliberately. It is reported for information only —
     * the same stance as iOS `authorizedWhenInUse`.
     */
    {
        key: 'location',
        fields: [
            { key: 'servicesEnabled', health: yesIsGood },
            { key: 'coarse', health: yesIsGood },
            { key: 'fine', health: yesIsGood },
            {
                key: 'background',
                health: yesIsGoodOtherwiseNeutral,
                minOsVersion: '10'
            }
        ]
    },
    /**
     * Android Bluetooth. As on iOS, nothing here is ever flagged — it only
     * matters to customers using our wearable.
     *
     * Deliberately two rows, mirroring what the user is actually shown:
     *
     * `BLUETOOTH` and `BLUETOOTH_ADMIN` (Android 11 and below) are install-time
     * normal permissions. They are granted automatically, never surfaced in
     * settings and cannot be revoked, so reporting them tells an admin nothing
     * — they are omitted entirely. Before Android 12 the only user-controlled
     * pieces are the adapter toggle and, for BLE scanning, Location.
     *
     * From Android 12 the runtime permissions BLUETOOTH_CONNECT / _SCAN /
     * _ADVERTISE all sit in the NEARBY_DEVICES group, and Android grants a
     * whole group from one prompt — the user sees a single "Nearby devices"
     * decision, so that is what we show. We only combine CONNECT and SCAN;
     * ADVERTISE is excluded because we never advertise (the phone is the
     * central, the wearable the peripheral) and it may not even be declared.
     */
    {
        key: 'bluetooth',
        fields: [
            {
                key: 'bluetoothEnabled',
                health: yesIsGoodOtherwiseNeutral
            },
            {
                key: 'nearbyDevices',
                derive: (values) =>
                    allGranted(values, ['bluetoothConnect', 'bluetoothScan']),
                health: yesIsGoodOtherwiseNeutral,
                minOsVersion: '12'
            }
        ]
    },
    /**
     * Android Notifications.
     *
     * `notificationsEnabled` and `postNotifications` are collapsed into one row
     * for the same reason as Nearby Devices: from Android 13 denying the
     * POST_NOTIFICATIONS prompt is exactly what turns notifications off, so the
     * user made a single decision and two rows would invent a distinction.
     * Before 13 there is no runtime permission and only the app-settings toggle
     * exists, which `allGranted` handles by ignoring the unreported source.
     *
     * Do Not Disturb is never flagged. `notificationPolicyAccess` (may the app
     * change DND) and `bypassDnd` (do our channels cut through it) are shown for
     * information only — DND is the user's call, and this mirrors the stance on
     * iOS `critical`.
     *
     * `scheduleExactAlarms` (Android 12+) is a warning when off: reminders
     * still fire, but Android may batch them by several minutes, which matters
     * for a timeout the worker is relying on.
     */
    {
        key: 'notifications',
        fields: [
            {
                key: 'notificationsAllowed',
                derive: (values) =>
                    allGranted(values, [
                        'notificationsEnabled',
                        'postNotifications'
                    ]),
                health: yesIsGood
            },
            {
                key: 'scheduleExactAlarms',
                health: yesIsGoodNoIsWarning,
                minOsVersion: '12'
            },
            {
                key: 'notificationPolicyAccess',
                health: yesIsGoodOtherwiseNeutral
            },
            {
                key: 'bypassDnd',
                health: yesIsGoodOtherwiseNeutral,
                minOsVersion: '9'
            }
        ]
    },
    /**
     * Android Battery & Power — in practice the most common cause of a missed
     * reminder on Android, since the OS kills or defers background work long
     * before it tells the user anything.
     *
     *   - `backgroundRestricted` (Android 9+) is the hardest stop, and the only
     *     failure here: the user has explicitly barred the app from running in
     *     the background at all.
     *   - `ignoringBatteryOptimizations` is NOT flagged when off. It leaves the
     *     app subject to Doze, but that is the default state for most handsets
     *     and delivery still works, so flagging it would mark the majority of a
     *     healthy fleet. Shown so support can see whether the exemption was
     *     ever granted.
     *   - `powerSavingMode` is a transient device state rather than a setting
     *     we control, but it throttles the same background work, so it is worth
     *     a warning while it is on.
     *
     * The `network` and `storage` groups the API also returns are deliberately
     * not listed. INTERNET is an install-time normal permission — always
     * granted, never revocable, so it cannot tell an admin anything (the same
     * reasoning that removed the legacy Bluetooth permissions). Storage has no
     * bearing on locating a worker or alerting them.
     */
    {
        key: 'batteryAndPower',
        fields: [
            {
                key: 'ignoringBatteryOptimizations',
                health: yesIsGoodOtherwiseNeutral
            },
            {
                key: 'backgroundRestricted',
                health: noIsGoodYesIsBad,
                minOsVersion: '9'
            },
            { key: 'powerSavingMode', health: noIsGood }
        ]
    }
];
